import { type PropsWithChildren, type ReactNode, useCallback } from "react";

import type {
  TooltipContentProps,
  TooltipProps as RadixTooltipProps,
  TooltipProviderProps,
  TooltipTriggerProps,
} from "@radix-ui/react-tooltip";
import {
  Tooltip as TooltipCore,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@vega-core/components/shadcn/tooltip";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { HtmlContent } from "../HtmlContent/HtmlContent";

const DEFAULT_TOOLTIP_SIDE_OFFSET = 8;

export type TooltipProps = {
  className?: string;
  content?: ReactNode | string;
  title?: ReactNode | string;
  providerProps?: Omit<TooltipProviderProps, "children">;
  rootProps?: Omit<RadixTooltipProps, "children">;
  triggerProps?: Omit<TooltipTriggerProps, "children">;
  contentProps?: Omit<TooltipContentProps, "children">;
  clickToOpen?: boolean;
};

export function Tooltip({
  children,
  className,
  content,
  title,
  providerProps,
  rootProps,
  triggerProps = { asChild: true },
  contentProps,
  clickToOpen = false,
}: PropsWithChildren<TooltipProps>) {
  const { toggle, isOpen, setTrue } = useBoolean();
  const handleClick = useCallback(
    () => (clickToOpen ? setTrue() : undefined),
    [clickToOpen, setTrue],
  );

  // Respect explicitly set open prop from rootProps, otherwise use internal state
  const isControlled = rootProps?.open !== undefined;
  const openState = isControlled ? rootProps.open : isOpen;

  // Early return if no content or title is provided
  if (!isTruthy(content) && !isTruthy(title)) {
    return children;
  }

  return (
    <TooltipProvider {...providerProps}>
      <TooltipCore {...rootProps} onOpenChange={toggle} open={openState}>
        <TooltipTrigger {...triggerProps} onClick={handleClick}>
          {children}
        </TooltipTrigger>
        <TooltipContent
          align="center"
          className={cnTwMerge(
            "flex flex-col gap-2 text-text/primary bg-background/primary p-2 drop-shadow-tooltip text-left rounded-md border border-border/secondary",
            className,
          )}
          data-testid="vega-tooltip-root"
          data-tooltip-id="vega-tooltip"
          side="top"
          sideOffset={DEFAULT_TOOLTIP_SIDE_OFFSET}
          {...contentProps}
        >
          {isTruthy(title) ? (
            <span className="body-small-semibold text-text/primary">
              <HtmlContent content={title} />
            </span>
          ) : null}
          {isTruthy(content) ? (
            <span className="body-small-regular whitespace-pre-wrap text-text/primary">
              <HtmlContent content={content} />
            </span>
          ) : null}
          <TooltipArrow className="fill-background/primary" />
        </TooltipContent>
      </TooltipCore>
    </TooltipProvider>
  );
}
